import React, { Dispatch, SetStateAction, useMemo, useState } from "react";
import classnames from "classnames";
import styles from "./NavStrip.module.scss";
import Clickable, { ClickableAction } from "@unum/clickable";
import Spring from "@unum/spring";
import ClickAwayListener from "react-click-away-listener";
import WhiteChevron from "./assets/chevron-down-w.inline.svg";
import BlueChevron from "./assets/chevron-down-b.inline.svg";
import WhitePadlock from "./assets/padlock-w.inline.svg";
import BluePadlock from "./assets/padlock-b.inline.svg";

type MenuItem = {
  label: React.ReactNode;
  action?: ClickableAction;
  links?: MenuItem[];
  isActive?: boolean;
};

type NavMenuProps = {
  label?: React.ReactNode;
  menuItems: MenuItem[];
  isOpen?: boolean;
  isSpread?: boolean;
  setOpenMenu?: Dispatch<SetStateAction<string | null>>;
  isDesktopOnly?: boolean;
  hideChevron?: boolean;
  customIcon?: {
    light: React.ReactNode;
    dark: React.ReactNode;
  };
  menuId?: string | null;
};

export type Props = {
  primaryMenu: Pick<MenuItem, "label" | "action" | "isActive">[];
  secondaryMenu: Pick<MenuItem, "label" | "action">[];
  loginMenu?: Pick<MenuItem, "label" | "links">;
};

const NavMenu = ({
  label,
  menuItems,
  isOpen,
  isSpread,
  menuId,
  setOpenMenu,
  isDesktopOnly,
  hideChevron,
  customIcon
}: NavMenuProps) => {
  const handleClickAway = () => {
    if (isOpen) {
      setOpenMenu(null);
    }
  };

  return (
    <div
      className={classnames(styles["nav-menu"], {
        [styles["nav-menu--desktop-only"]]: isDesktopOnly
      })}
    >
      <button
        // @note: this button is only visible when the menu is in dropdown form
        type="button"
        onClick={(event: React.SyntheticEvent): void => {
          if (!isOpen && setOpenMenu) {
            event.stopPropagation();
            setOpenMenu(menuId);
          }
        }}
        className={classnames(styles["button"], "button", {
          [styles["button--spread"]]: isSpread,
          [styles["button--open"]]: isOpen
        })}
      >
        {customIcon ? (
          <div className={classnames(styles["icon"], styles["icon--left"])}>
            <div className={styles["icon--white"]}>{customIcon.light}</div>
            <div className={styles["icon--blue"]}>{customIcon.dark}</div>
          </div>
        ) : null}
        {label}
        {!hideChevron ? (
          <div className={classnames(styles["icon"], styles["icon--right"])}>
            <div className={styles["icon--white"]}>
              <WhiteChevron />
            </div>
            <div
              className={classnames(styles["icon--blue"], {
                [styles["icon--open"]]: isOpen
              })}
            >
              <BlueChevron />
            </div>
          </div>
        ) : null}
      </button>

      {/* @note: This list will be either visible as dropdown items or as buttons in the menu bar */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <ul
          // @note: In theory only on mobile but we don't have access.
          aria-haspopup={!isSpread}
          className={classnames("list", styles["menu-list"], {
            [styles["menu-list--spread"]]: isSpread,
            [styles["menu-list--open"]]: isOpen
          })}
        >
          {menuItems.map((link, index) => (
            <li
              key={`menu-item-${index}`}
              className={classnames(styles["menu-item"], {
                [styles["menu-item--spread"]]: isSpread,
                [styles["menu-item--active"]]: link.isActive
              })}
            >
              <Clickable {...link.action} className={styles["menu-link"]}>
                {link.label}
              </Clickable>
            </li>
          ))}
        </ul>
      </ClickAwayListener>
    </div>
  );
};

const NavStrip = ({ primaryMenu, secondaryMenu, loginMenu }: Props) => {
  const [openMenu, setOpenMenu] = useState<"primary" | "login" | null>(null);
  const primaryMenuActiveLabel = useMemo(() => {
    const activeMenuItem = primaryMenu.find(({ isActive }) => isActive);

    return activeMenuItem?.label || primaryMenu[0].label;
  }, [primaryMenu]);

  return (
    <nav className={styles["NavStrip"]}>
      <Spring>
        <div className={styles["wrapper"]}>
          <div className={styles["column"]}>
            <NavMenu
              label={primaryMenuActiveLabel}
              menuItems={primaryMenu}
              isOpen={openMenu === "primary"}
              isSpread
              menuId="primary"
              setOpenMenu={setOpenMenu}
            />
          </div>
          <div
            className={classnames(
              styles["column"],
              styles["column--right-aligned"]
            )}
          >
            <NavMenu menuItems={secondaryMenu} isSpread isDesktopOnly />
          </div>

          {loginMenu && (
            <div
              className={classnames(
                styles["column"],
                styles["column--right-aligned"]
              )}
            >
              <NavMenu
                label={loginMenu.label}
                menuItems={loginMenu.links}
                isOpen={openMenu === "login"}
                menuId="login"
                setOpenMenu={setOpenMenu}
                hideChevron
                customIcon={{
                  light: <WhitePadlock />,
                  dark: <BluePadlock />
                }}
              />
            </div>
          )}
        </div>
      </Spring>
    </nav>
  );
};

export default NavStrip;
